import React, { useEffect, useState } from 'react';
import { View, StyleSheet, AccessibilityInfo } from 'react-native';
import { Text } from '@/components/common/text';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withSpring,
  withDelay,
  useReducedMotion,
} from 'react-native-reanimated';
import { SVG_COMPONENTS } from '@/assets/admin';
// Direct-path import (not the barrel) so unit tests don't pull the whole
// UnifiedModal barrel (gesture-handler/sheet) into the module graph.
import { ModalButton } from '@/components/common/UnifiedModal/ModalButton';
import { Spinner } from '@/components/common/Spinner';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { maintenanceSubmitStrings as S } from '@/constants/residents/strings';

const LargeSuccessIcon = SVG_COMPONENTS.LARGE_SUCCESS;
const STAGE_SWITCH_MS = 900;

interface MaintenanceSubmitStatusProps {
  phase: 'submitting' | 'success';
  hasAttachments: boolean;
  onDone: () => void;
}

export const MaintenanceSubmitStatus: React.FC<MaintenanceSubmitStatusProps> = ({
  phase,
  hasAttachments,
  onDone,
}) => (
  <View style={styles.container}>
    {phase === 'submitting' ? (
      <SubmittingView hasAttachments={hasAttachments} />
    ) : (
      <SuccessView onDone={onDone} />
    )}
  </View>
);

const SubmittingView: React.FC<{ hasAttachments: boolean }> = ({ hasAttachments }) => {
  const { colors } = useThemeColors();
  const [stage, setStage] = useState<'uploading' | 'submitting'>(
    hasAttachments ? 'uploading' : 'submitting',
  );

  useEffect(() => {
    if (!hasAttachments) return;
    const t = setTimeout(() => setStage('submitting'), STAGE_SWITCH_MS);
    return () => clearTimeout(t);
  }, [hasAttachments]);

  const title = stage === 'uploading' ? S.uploadingPhotos : S.submitting;

  useEffect(() => {
    AccessibilityInfo.announceForAccessibility(title);
  }, [title]);

  return (
    <View style={styles.content}>
      <Spinner size={44} />
      <View style={styles.textGroup}>
        <Text style={[styles.title, { color: colors.text.primary }]}>{title}</Text>
        <Text style={[styles.subtitle, { color: colors.text.dim }]}>{S.submittingSubtitle}</Text>
      </View>
    </View>
  );
};

const SuccessView: React.FC<{ onDone: () => void }> = ({ onDone }) => {
  const { colors } = useThemeColors();
  const reduceMotion = useReducedMotion();

  const iconOpacity = useSharedValue(0);
  const iconScale = useSharedValue(reduceMotion ? 1 : 0.6);
  const textOpacity = useSharedValue(0);
  const textTranslate = useSharedValue(reduceMotion ? 0 : 8);
  const buttonOpacity = useSharedValue(0);

  useEffect(() => {
    AccessibilityInfo.announceForAccessibility(S.successTitle);
    if (reduceMotion) {
      iconOpacity.value = withTiming(1, { duration: 150 });
      textOpacity.value = withTiming(1, { duration: 150 });
      buttonOpacity.value = withTiming(1, { duration: 150 });
      return;
    }
    iconOpacity.value = withTiming(1, { duration: 200 });
    iconScale.value = withSpring(1, { damping: 12, stiffness: 180 });
    textOpacity.value = withDelay(120, withTiming(1, { duration: 220 }));
    textTranslate.value = withDelay(120, withTiming(0, { duration: 220 }));
    buttonOpacity.value = withDelay(260, withTiming(1, { duration: 220 }));
  }, [reduceMotion, iconOpacity, iconScale, textOpacity, textTranslate, buttonOpacity]);

  const iconStyle = useAnimatedStyle(() => ({
    opacity: iconOpacity.value,
    transform: [{ scale: iconScale.value }],
  }));
  const textStyle = useAnimatedStyle(() => ({
    opacity: textOpacity.value,
    transform: [{ translateY: textTranslate.value }],
  }));
  const buttonStyle = useAnimatedStyle(() => ({ opacity: buttonOpacity.value }));

  return (
    <View style={styles.content}>
      <Animated.View style={[styles.iconContainer, iconStyle]}>
        <LargeSuccessIcon width={100} height={100} />
      </Animated.View>
      <Animated.View style={[styles.textGroup, textStyle]}>
        <Text style={[styles.title, { color: colors.text.primary }]}>{S.successTitle}</Text>
        <Text style={[styles.subtitle, { color: colors.text.dim }]}>{S.successSubtitle}</Text>
      </Animated.View>
      <Animated.View style={[styles.doneWrapper, buttonStyle]}>
        <ModalButton label={S.done} variant="primary" onPress={onDone} animateOnPress={false} />
      </Animated.View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
    paddingVertical: 32,
  },
  content: {
    alignItems: 'center',
    gap: 16,
    maxWidth: 360,
  },
  iconContainer: {
    marginBottom: 4,
  },
  textGroup: {
    alignItems: 'center',
    gap: 8,
  },
  title: {
    fontSize: 24,
    fontWeight: '600',
    lineHeight: 32,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
    textAlign: 'center',
  },
  doneWrapper: {
    flexDirection: 'row',
    width: 220,
    maxWidth: '100%',
    marginTop: 8,
  },
});
